import { getExtractionDetail } from './procedural-helpers';
import { ExtractionPhase, ExtractionState } from '@/api/hooks/useProceduralStream';
import { Icon } from '@/new/components/icon';
import { cn } from '@/new/lib/utils';

interface ExtractionProgressBannerProps {
  state: ExtractionState;
}

const PHASE_LABELS: Record<ExtractionPhase, string> = {
  reading: 'Reading your timetable',
  extracting: 'Extracting procedural events',
  merging: 'Reviewing against the existing events',
  saving: 'Saving suggestions',
  done: 'Extraction complete',
  failed: 'Extraction failed',
};

export function ExtractionProgressBanner({ state }: ExtractionProgressBannerProps) {
  const label = PHASE_LABELS[state.phase];
  const detail = getExtractionDetail(state);
  const isFailed = state.phase === 'failed';
  const isDone = state.phase === 'done';
  const isWorking = !isFailed && !isDone;

  return (
    <div
      className={cn(
        'mb-5 flex items-center gap-3 rounded-md border px-4 py-3',
        isFailed && 'border-red-200 bg-red-50',
        isDone && 'border-teal-200 bg-teal-50',
        isWorking && 'border-sky-200 bg-sky-50',
      )}
    >
      <Icon
        className={cn(
          'shrink-0',
          isWorking && 'lucide--loader-2 animate-spin text-sky-600',
          isDone && 'lucide--check-circle-2 text-teal-600',
          isFailed && 'lucide--triangle-alert text-red-600',
        )}
      />
      <p
        className={cn(
          'min-w-0 flex-1 truncate text-sm',
          isFailed && 'text-red-800',
          isDone && 'text-teal-800',
          isWorking && 'text-sky-800',
        )}
      >
        <span className="font-medium">{label}</span>
        {detail && (
          <span
            className={cn('ml-1', isFailed && 'text-red-600', isDone && 'text-teal-600', isWorking && 'text-sky-600')}
          >
            · {detail}
          </span>
        )}
      </p>
    </div>
  );
}
